import { useEffect, useState, type FunctionComponent } from "react";

import { useModalizeStore, SelectableAssetDeposit } from "@components";

import { LegalAgreements, PrivacyPolicy, TermsAndServices } from "@sections";

import { bi, sn, getBlockLink } from "@helpers";

import { useEscrowStore } from "@stores/escrow";
import { useLegalStore } from "@stores/legal";
import { usePointsStore } from "@stores/points";

import { getChainedTokens, type TokenDefinition } from "@bracket/addressbook";

import { Button } from "@bracket/react-button";
import { useMessengerStore } from "@bracket/react-messenger";
import { Loader } from "@bracket/react-loader";
import { bracket as BRKT } from "@packageconfig";

import type { NumericString } from "@types";

import "./SwapStake.scss";

import { useChainId } from "wagmi";

// biome-ignore lint/complexity/noBannedTypes: i want an empty object...
export declare type SwapStakeProps = {};

export const SwapStake: FunctionComponent<SwapStakeProps> = ({}) => {
  const classes = () => ["section-set-stake"].join(" ");
  const chainId = useChainId();
  const [showInvalid, setShowInvalid] = useState(false);

  const { modalize, dismiss } = useModalizeStore();

  const {
    busy,
    initialized,
    swap,
    getTokenBalance,
    getTokenAvailable,
    swapDepositAmount,
    setSwapDepositAmount,
    swapDepositToken,
    setSwapDepositToken,
    swapWithdrawAmount,
    setSwapWithdrawAmount,
    swapWithdrawToken,
    setSwapWithdrawToken,
    toUSD,
    tokens,
    reset,
    applyMaxSwap,
    swapWithdrawAmountValid,
    swapWithdrawAmountMessage,
    quote_issue,
		slippage,
		impact,
	} = useEscrowStore();

  const { accepted_terms, setTermsAccepted } = useLegalStore();

  const { sendMessage } = useMessengerStore();

  const { shouldSign, sign } = usePointsStore();

  const swapToTokens = getChainedTokens(chainId, { swapto: true });

  const onWithdrawTokenChange = (token: TokenDefinition) => {
    reset();
    setSwapWithdrawToken(token);
  };

  const onWithdrawAmountChange = (amount: NumericString) =>
    setSwapWithdrawAmount(amount);
  const onDepositTokenChange = (token: TokenDefinition) =>
    setSwapDepositToken(token);
  const onDepositAmountChange = (amount: NumericString) =>
    setSwapDepositAmount(amount);
  const onWithdrawBlur = () => setShowInvalid(true);
  const onWithdrawFocus = () => setShowInvalid(false);

  const onTermsAndServices = () => {
    modalize({
      component: TermsAndServices,
      title: "Terms of service",
      blur: true,
    });
  };

  const onPrivacyPolicy = () => {
    modalize({
      component: PrivacyPolicy,
      title: "Privacy Policy",
      blur: true,
    });
  };

  const revealTerms = () => {
    const buttons: any[] = [
      {
        button: Button,
        buttonProps: {
          label: "Close",
          onClick: onReject,
          variant: "outline",
          flexible: true,
        },
      },
      {
        button: Button,
        buttonProps: {
          label: "I accept	",
          onClick: onAcceptAgreements,
          variant: "outline",
          flexible: true,
        },
      },
    ];
    modalize({
      component: LegalAgreements,
      title: "Terms of service / Privacy Policy",
      subtitle:
        "You must read the document in it's entirety to continue (scroll)",
      blur: true,
      buttons,
    });
  };

  const onAcceptTerms = () => {
    dismiss();
    const buttons: any[] = [
      {
        button: Button,
        buttonProps: {
          label: "Close",
          onClick: onReject,
          variant: "outline",
          flexible: true,
        },
      },
      {
        button: Button,
        buttonProps: {
          label: "I accept	",
          onClick: onAcceptPolicy,
          variant: "outline",
          flexible: true,
        },
      },
    ];
    modalize({
      component: PrivacyPolicy,
      title: "Privacy Policy",
      subtitle:
        "You must read the document in it's entirety to continue (scroll)",
      blur: true,
      buttons,
    });
  };

  const onAcceptPolicy = () => {
    setTermsAccepted(true);
    dismiss();
    onSwap(true);
  };

  const onAcceptAgreements = () => {
    setTermsAccepted(true);
    dismiss();
    onSwap(true);
  };

  const onReject = () => {
    dismiss();
  };

  const onSwap = (force = false) => {
    (async () => {
      if (accepted_terms || force === true) {
        const [signage, user_message] = shouldSign();
        const signer = signage ? sign : null;
        const response = await swap(signer);

        const { code, message, status: severity } = response;

        switch (code) {
          case "SUCCESS":
            sendMessage({
              title: "Stake",
              severity,
              message,
              blockChainLink: getBlockLink(response.hash),
            });
            if (user_message) {
              sendMessage({
                severity: "warning",
                message: user_message,
                title: "Referral information:",
              });
            }
            window.history.pushState({}, "", "/stake/balances");
            reset();
            return;

          case "CANCELLED":
          case "UNDERFUND":
          case "UNHANDLED":
            sendMessage({ title: "Stake", severity, message });
            reset();
            return;

          default:
            throw new Error(`SwapStake::unhandled response code "${code}"`);
        }
      }

      revealTerms();
    })();
  };

  const label = () =>
    (showInvalid || quote_issue) && !swapWithdrawAmountValid
      ? swapWithdrawAmountMessage
      : "Confirm swap and stake";
  const disabled = (showInvalid || quote_issue) && !swapWithdrawAmountValid;

  useEffect(() => {
    reset();
  }, [reset]);

  if (!initialized) {
    return (
      <div className="loading">
        <Loader />
      </div>
    );
  }
  if (!swapWithdrawToken)
    throw new Error(
      "@bracket/swap :: SwapStake requires the swapWithdrawalToken be set"
    );
  if (!swapDepositToken)
    throw new Error(
      "@bracket/swap :: SwapStake requires the swapDepositToken be set"
    );

  return (
    <div className={classes()}>
      <SelectableAssetDeposit
        applyMax={applyMaxSwap}
        amount={swapWithdrawAmount}
        balance={getTokenAvailable(swapWithdrawToken)}
        balanceUSD={toUSD(swapWithdrawToken, bi(sn(swapWithdrawAmount)))}
        busy={busy}
        chain={chainId}
        interactive={!busy}
        onTokenChange={onWithdrawTokenChange}
        onValueChange={onWithdrawAmountChange}
        showBalance
        token={swapWithdrawToken}
        tokens={tokens}
        onBlur={onWithdrawBlur}
        onFocus={onWithdrawFocus}
        hackvar="sw"
      />

      <SelectableAssetDeposit
        token={swapDepositToken}
        tokens={swapToTokens}
        chain={chainId}
        amount={swapDepositAmount}
        onTokenChange={onDepositTokenChange}
        onValueChange={onDepositAmountChange}
        balance={getTokenBalance(swapDepositToken)}
        balanceUSD={toUSD(swapDepositToken, bi(sn(swapDepositAmount)))}
        exclude={["ETH", "WETH", "ankrETH"]}
        interactiveInput={false}
        showBalance
        showMax={false}
        busy={busy}
        hackvar="sd"
      />
			<div className="details">
				<p>Max Slippage {(Number(BRKT.slippage) * 100).toFixed(0)}%</p>
				{/* <p>Slippage {impact === "n/a" ? impact : Number.parseFloat(impact).toFixed(5)}</p> */}
			</div>
      <Button
        className="stake-btn"
        label={label()}
        onClick={onSwap}
        busy={busy}
        disabled={disabled}
        width="auto"
      />

      <div className="disclaimer">
        <p>
          By using Bracket services, you agree to the{" "}
          <a href="#" onClick={onTermsAndServices}>
            Terms of Service
          </a>{" "}
          and consent to our{" "}
          <a href="#" onClick={onPrivacyPolicy}>
            Privacy Policy
          </a>
          .
        </p>
        <br />
        <p>You can withdraw at any time and keep the [BARS] you have earned.</p>
      </div>
    </div>
  );
};
